import { TableRow } from '@mui/material';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Case } from '../../../../shared/api/hscan/case';
import { genderToString } from '../../../../shared/utils';
import { ImageView } from '../../common/components/ImageView';
import {
  PresentationalTableProps,
  TableInformation,
  createTableElementInformationContext,
  createTableInformationContext,
} from '../../common/components/SortableTable/PresentationalTable';
import {
  BasicTable,
  LoadingIndicator,
} from '../../common/components/SortableTable/RenderTable';
import { HscanTableCell } from '../../common/components/SortableTable/Table';
import { useModalityLabel } from '../../hooks/modalityHook';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { MobileImageTable } from './MobileImageTable';

// 이걸 어딘가 공통된 곳으로 옮길지, 아니면 매 컴포넌트마다 컨텍스트를 새로 정의할지 의논 필요
export const { TableContext, TableInformationProvider } =
  createTableInformationContext<Case, 'caseId'>();
export const { TableElementContext, TableElementInformationProvider } =
  createTableElementInformationContext<Case, 'caseId'>(TableContext);

export const ImageListTable = (
  props: Omit<PresentationalTableProps<Case, 'caseId'>, 'identifyKey'>,
) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.myImageList.table',
  });
  const { t: globalT } = useTranslation();
  const modalityLabel = useModalityLabel();

  const columnHelper = useMemo(() => createColumnHelper<Case>(), []);
  const cellOptions = useMemo(
    () => [
      columnHelper.accessor('contentIds', {
        header: `${t('header.image')}`,
        cell: (context: unknown) => {
          const {
            getValue,
            width = '40px',
            height = '40px',
          } = context as CellContext<Case, string[]> & {
            width: string;
            height: string;
          }; // TODO: 뭔가 더 좋은 방법이 있지 않을까 싶다
          return (
            <ImageView
              width={width}
              height={height}
              imageId={getValue()?.[0] ?? ''}
            />
          );
        },
        meta: {
          cellStyle: {
            align: 'center',
            borderLeft: { style: 'solid' },
            borderRight: { style: 'solid' },
            isNonTextContent: true,
          },
          headerStyle: {
            align: 'center',
            borderLeft: { style: 'solid' },
            borderRight: { style: 'solid' },
          },
        },
      }),
      columnHelper.accessor('requestedDate', {
        header: `${t('header.requestedDate')}`,
        cell: ({ getValue }) => {
          const value = getValue();
          return value
            ? moment(value).format(`${globalT('common.imageDateFormatString')}`)
            : '';
        },
      }),
      columnHelper.accessor('acceptedDate', {
        header: `${t('header.acceptedDate')}`,
        cell: ({ getValue }) => {
          const value = getValue();
          return value
            ? moment(value).format(`${globalT('common.imageDateFormatString')}`)
            : '';
        },
      }),
      columnHelper.accessor('institutionName', {
        header: `${t('header.institutionName')}`,
      }),
      columnHelper.accessor('patientName', {
        header: `${t('header.patientName')}`,
      }),
      columnHelper.accessor('patientSex', {
        header: t('header.patientSex') as string,
        cell: ({ getValue }) =>
          globalT(`common.gender.${genderToString(getValue())}`),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('birthDate', {
        header: t('header.birthDate') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('studyDate', {
        header: t('header.studyDate') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('modality', {
        header: t('header.modality') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
        cell: ({ getValue }) => modalityLabel(getValue()),
      }),
      columnHelper.accessor('bodyPart', {
        header: t('header.bodyPart') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
        cell: ({ getValue }) => getValue()?.join(', '),
      }),
    ],
    [columnHelper, t, globalT, modalityLabel],
  );

  const windowSize = useContext(WindowSizeContext);

  return (
    <TableInformationProvider
      {...props}
      cellOptions={cellOptions}
      identifyKey="caseId">
      {windowSize === WindowSize.MOBILE ? (
        <MobileImageTable />
      ) : (
        <BasicTable
          tableContext={TableContext}
          elementContext={TableElementContext}
          ElementProvider={TableElementInformationProvider}
          renderLoading={LoadingIndicator}
          renderEmpty={props => <EmptyIndicator {...props} />}
        />
      )}
    </TableInformationProvider>
  );
};

const EmptyIndicator = (props: TableInformation<Case, 'caseId'>) => {
  const { cellOptions } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.myImageList.table',
  });
  return (
    <TableRow>
      <HscanTableCell colSpan={cellOptions.length + 1} align="center">
        {t('message.empty')}
      </HscanTableCell>
    </TableRow>
  );
};
